import styled from '@emotion/styled';
import { Select, Slider, Spin, Table } from 'antd';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import ExpansionBar from 'components/molecules/ExpansionBar';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import Legend from 'components/molecules/Legend';
import Loader from 'components/molecules/Loader';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import BumpChartContainer, { sliderPaddingMapping } from 'components/organism/BumpReport/BumpChartContainer';
import PromoComparisonContainer from 'components/organism/BumpReport/PromoComparisonContainer';
import { commonClassName } from 'components/pages/PromoScorecard';
import { colorConstants } from 'config/colorConstants';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import _ from 'lodash';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import tw from 'twin.macro';
import { getBumpReportColumns } from 'utils/getBumpReportColumns';
import {
    calculateContainerHeight,
    calculateFontSize,
    fetchCurrency,
    fetchUnit,
    formatUnit,
    getConfig,
    parseAttributeType,
} from 'utils/helperMethods';
import { yAxisLabelClass } from '../GrowthAnalysis/ExpandedView';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    BUMP_REPORT_BAR_SLIDER_CHANGE,
    BUMP_REPORT_CHART_COPY_CLICKED,
    BUMP_REPORT_CHART_EXPORT_CLICKED,
    BUMP_REPORT_DROPDOWN_CHANGE,
    BUMP_REPORT_GROUPBY_DROPDOWN_CHANGE,
    BUMP_REPORT_GROUPBY_KPI_DROPDOWN_CHANGE,
} from 'utils/mixpanelEventNames';
import dayjs from 'dayjs';
import DownSvg from 'assets/svg/Down';
import SelectionDropDown from 'components/molecules/SelectionDropDown';
import TileButton from 'components/atoms/TileButton';
import { bumpReportUrls } from 'config/urls';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { useFetchBumpReportGraphRQ } from 'hooks/bumpReport/useFetchBumpReportGraphRQ';
import { useFetchPromoCalenderRQ } from 'hooks/bumpReport/useFetchPromoCalenderRQ';
import { useDebounceLoading } from 'hooks/general/useDebounceLoading';
import { useFetchGraphExportRQ } from 'hooks/bumpReport/useFetchGraphExportRQ';
import { LoadingOutlined } from '@ant-design/icons';
import GrowthAnalysisTable from 'components/molecules/GrowthAnalysis/GrowthAnalysisTable';

interface Props {
    tileData: TileData;
    groupByOptionSelected: any;
    optionSelected: any;
    setGroupByOptionSelected: React.Dispatch<any>;
    setOptionSelected: React.Dispatch<any>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    showAllLabels: boolean;
}
export type TileData = {
    absTile: TileItem;
    volTile: TileItemVol;
    bumpReportTile: TileItem;
};
type TileItem = {
    key: string;
    label: string;
};
type TileItemVol = {
    key: string;
    label: string;
    dataKey: string;
};
const rowKey = 'key';
const multiSelectionDropdownLabel = 'Multiple KPIs';

export const promoCalenderId = 'promo-report-calender';

const promoComparisonSubHeader = 'Comparison';

const bumpChartHeader = 'Bump Chart';

export const CustomSelect = styled(Select)`
    ${tw`border border-purpleFont rounded text-[13px] text-headerColor`}
    .ant-select-selector {
        box-shadow: none !important;
        border: none !important;
        height: 100% !important;
        display: flex;
        align-items: center;
    }
    .ant-select-arrow {
        ${tw`text-headerColor`}
    }
`;

export const CustomSlider = styled(Slider)`
    .ant-slider-handle {
        border: 2.5px solid ${colorConstants.violet};
    }
    .ant-slider-track {
        background-color: ${colorConstants.violet};
    }
    .ant-slider-dot-active {
        border-color: ${colorConstants.violet};
    }
    :hover .ant-slider-track {
        background-color: ${colorConstants.violet};
    }
    :hover .ant-slider-handle:not(.ant-tooltip-open) {
        border-color: ${colorConstants.violet};
    }
`;

export const BumpReportCalenderTable = styled(Table)`
    .ant-table-sticky-scroll {
        position: static;
        display: none;
    }
    .ant-table-sticky-holder {
        z-index: 5;
    }
    .ant-table-cell-row-hover {
        background: auto !important;
    }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 0px;
        height: 44px;
        border-right: none !important;
    }
    // .ant-table-header col:not(:last-child),
    // .ant-table-body col:not(:last-child) {
    //     min-width: 100px;
    // }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    border-radius: 4px;
    ${tw`border border-borderLinePurpuleAlt`}
`;

export const sliderFontSize = calculateFontSize(window.innerWidth, 11);
export const sliderWidth = calculateFontSize(window.innerWidth, 100);
const legendWidth = calculateFontSize(window.innerWidth, 60);
const legendTop = calculateFontSize(window.innerWidth, 95);
const bumpChartHeight = calculateContainerHeight(3);

export const calenderMinRowHeight = 70;

export const sliderMarks: any = {
    12: { label: '12W', style: { fontSize: sliderFontSize } },
    32: { label: '26W', style: { fontSize: sliderFontSize } }, //manipulating 32 with 26 because 26 needs to be shown at the center
    52: { label: '52W', style: { fontSize: sliderFontSize } },
};
const xAxisLabel = 'Dates';

const attributeTypeLabel = 'Group By level';
const kpiDropdownLabel = 'KPI Shown';

const MainSection: FC<Props> = ({
    setLoading,
    tileData,
    groupByOptionSelected,
    setGroupByOptionSelected,
    optionSelected,
    setOptionSelected,
    showAllLabels,
}) => {
    const { appConfig } = useContext(UserContext);
    const bumpReportConfig = getConfig('bumpReport', appConfig);
    const promoCalenderAttributeLevels = bumpReportConfig?.promoCalenderAttributeLevels;
    const valAbsUnitMapping: any = {
        val_abs: ['Sales', fetchCurrency(appConfig)],
        vol_abs: ['Sales', `(${fetchUnit(appConfig, 'volume')})`],
        val_percentage: ['Sales', '(%)'],
        vol_percentage: ['Volume Sales', '(%)'],
        units_abs: ['Sales', '(Units)'],
        units_percentage: ['Unit Sales', '(%)'],
    };
    const mapAbsVolTile = (tileData: any) => {
        return valAbsUnitMapping[`${tileData.volTile.key}_${tileData.absTile.key}`];
    };

    const {
        allDimAndAttributeTypeList,
        idForRestOfComponent,
        timePeriodLoading,
        timeSelectionLabel,
        allCheckedAttribute,
        timePeriodSelection,
        idForRestOfComponentLoading,
    } = useContext(FilterContextData);

    const promoTypeColorMapping = bumpReportConfig.promoTypeColorMapping;
    const bumpChartY2AxisOptions = bumpReportConfig.bumpChartY2AxisOptions?.[tileData?.volTile.key] || [];
    const volGroupByOptionsMapping = bumpReportConfig.volGroupByOptionsMapping;
    const stackedColors = bumpReportConfig.stackedColors;
    const groupByKey = bumpReportConfig.groupByKey;
    const enablePromoCalender = bumpReportConfig.enablePromoCalender;
    const bumpReportTile1Data = bumpReportConfig?.bumpReportTile1Data;
    const bumpReportTile2Data = bumpReportConfig?.bumpReportTile2Data;

    const [isCalenderExpanded, setIsCalenderExpanded] = useState(true);
    const [attributeTypeSelected, setAttributeTypeSelected] = useState(bumpChartY2AxisOptions[0]);
    const [attributeTypeList, setAttributeTypeList] = useState<any[]>([]);
    const [selectedBumpReportTile1Data, setSelectedBumpReportTile1Data] = useState(bumpReportTile1Data?.[0]);
    const [selectedBumpReportTile2Data, setSelectedBumpReportTile2Data] = useState(bumpReportTile2Data?.[0]);
    const refForGraph = useRef<any>();

    const [sliderValue, setSliderValue] = useState(26);
    const [chartConfig, setChartConfig] = useState({
        barWidth: 20,
        width: 1093,
    });
    const time_period = timePeriodSelection && JSON.parse(timePeriodSelection);

    //react query variables
    const [comparisonFirstVariable, setComparisonFirstVariable] = useState<any>({});
    const [comparisonSecondVariable, setComparisonSecondVariable] = useState<any>({});
    const [groupByComparisonFirstVariable, setGroupByComparisonFirstVariable] = useState<any>({});
    const [groupByComparisonSecondVariable, setGroupByComparisonSecondVariable] = useState<any>({});
    const [bumpReportCalenderVariable, setBumpReportCalenderVariable] = useState<any>({
        page: 1,
    });
    const isMedia = selectedBumpReportTile1Data?.key === 'media';
    const isGroupBy = tileData.bumpReportTile.key === groupByKey;

    const selectedOptionsWithLabels = useMemo(
        () => bumpChartY2AxisOptions.filter((item: any) => optionSelected.includes(item.value)),
        [optionSelected],
    );

    //react queries
    const enabledVariable = Boolean(
        allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !idForRestOfComponentLoading &&
            !timePeriodLoading &&
            idForRestOfComponent,
    );

    const investmentTypeDropdownVariables = {
        time_period,
        filters: idForRestOfComponent,
    };
    //react query to fetch investment type values
    const {
        error: groupByDropdownError,
        loading: groupByDropdownLoading,
        data: groupByDropdownData,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: enabledVariable && isGroupBy && isMedia },
        queryKey: ['bump-report-dropdown', { ...investmentTypeDropdownVariables }],
        url: bumpReportUrls.mediaTypesUrl,
        variables: {
            ...investmentTypeDropdownVariables,
        },
        parserFunction: (data: any) => {
            if (data?.length === 0) throw 'No Data';
            const firstItem = { label: 'Media Attributed', value: 'media_incremental', index: 2 };
            const finalData = data?.map((item: any) => {
                return {
                    label: item,
                    value: _.snakeCase(item),
                    index: 2,
                };
            });
            return [firstItem, ...finalData];
        },
    });
    //tile 1 list
    const bumpReportTile1DataList = useMemo(() => {
        const updatedList = bumpReportTile1Data?.filter((item: any) =>
            item?.visibleTabs?.includes(tileData?.bumpReportTile?.key),
        );
        setSelectedBumpReportTile1Data(updatedList?.[0]);
        return updatedList || [];
    }, [tileData?.bumpReportTile]);

    const incrementalPromoVariables = {
        time_period,
        filters: idForRestOfComponent,
        data_type: tileData.bumpReportTile.key === 'increments' ? 'base_incremental' : 'promo',
        metric_type: tileData.volTile.dataKey,
        investment_categories: bumpReportTile1DataList?.map((item: any) => item?.key),
        investment_type: selectedBumpReportTile1Data?.key,
        data_level: selectedBumpReportTile2Data?.key,
        isComparison: false,
    };

    const incrementalPromoExportVariables = {
        ...incrementalPromoVariables,
        kpi_list: bumpChartY2AxisOptions?.map((item: any) => item?.value) || [],
    };

    const groupByVariables = {
        time_period,
        isComparison: false,
        filters: idForRestOfComponent,
        data_type:
            groupByOptionSelected?.index === 0
                ? 'base_incremental'
                : groupByOptionSelected?.index === 1
                ? 'promo'
                : 'group_by',
        metric: groupByOptionSelected?.value,

        dimension: attributeTypeSelected?.dimension || '',
        attribute_type: _.snakeCase(attributeTypeSelected?.label) || '',
    };

    const legendLabels = useMemo(() => {
        const stringifiedOptions = Array.isArray(selectedOptionsWithLabels)
            ? selectedOptionsWithLabels?.map((item) => item?.label)?.toString()
            : [];
        return stringifiedOptions || '';
    }, [selectedOptionsWithLabels]);

    //react query to fetch incremental and promo tab data
    const {
        data: incrementalData,
        loading: incrementalDataLoading,
        error: incrementalDataError,
    } = useFetchBumpReportGraphRQ({
        method: 'post',
        appConfig,
        queryConfig: { enabled: enabledVariable && !isGroupBy && !incrementalPromoVariables.isComparison },
        queryKey: ['bump-report-incremental-promo', incrementalPromoVariables],
        lineChartQueryConfig: { enabled: enabledVariable && !isGroupBy && !incrementalPromoVariables.isComparison },
        extras: {
            metric: selectedOptionsWithLabels,
            from: tileData.bumpReportTile.key,
            y2AxisLabel: selectedOptionsWithLabels?.length === 1 ? selectedOptionsWithLabels?.[0]?.label : legendLabels,
        },
        url: bumpReportUrls.incrementsPromoUrl,
        variables: {
            ...incrementalPromoVariables,
        },
    });

    //react query to fetch 1st comparison incremental and promo tab data
    const {
        data: firstComparisonIncrementalData,
        loading: firstComparisonIncrementalDataLoading,
        error: firstComparisonIncrementalDataError,
        tableData: firstComparisonIncrementalTableData,
    } = useFetchBumpReportGraphRQ({
        method: 'post',
        appConfig,
        queryConfig: { enabled: enabledVariable && !isGroupBy && Boolean(comparisonFirstVariable?.isComparison) },
        queryKey: ['bump-report-incremental-promo', comparisonFirstVariable],
        lineChartQueryConfig: { enabled: false },
        extras: {
            from: tileData.bumpReportTile.key,
            y2AxisLabel: selectedOptionsWithLabels?.length === 1 ? selectedOptionsWithLabels?.[0]?.label : legendLabels,
        },
        url: bumpReportUrls.incrementsPromoUrl,
        variables: {
            ...comparisonFirstVariable,
        },
    });

    //react query to fetch 2nd comparison incremental and promo tab data
    const {
        data: secondComparisonIncrementalData,
        loading: secondComparisonIncrementalDataLoading,
        error: secondComparisonIncrementalDataError,
        tableData: secondComparisonIncrementalTableData,
    } = useFetchBumpReportGraphRQ({
        method: 'post',
        appConfig,
        queryConfig: { enabled: enabledVariable && !isGroupBy && Boolean(comparisonSecondVariable?.isComparison) },
        queryKey: ['bump-report-incremental-promo', comparisonSecondVariable],
        lineChartQueryConfig: { enabled: false },
        extras: {
            from: tileData.bumpReportTile.key,
            y2AxisLabel: selectedOptionsWithLabels?.length === 1 ? selectedOptionsWithLabels?.[0]?.label : legendLabels,
        },
        url: bumpReportUrls.incrementsPromoUrl,
        variables: {
            ...comparisonSecondVariable,
        },
    });

    //react query to fetch groupby tab data
    const {
        data: groupByData,
        loading: groupByLoading,
        error: groupByError,
    } = useFetchBumpReportGraphRQ({
        method: 'post',
        appConfig,
        queryConfig: { enabled: enabledVariable && isGroupBy && !Boolean(groupByVariables?.isComparison) },
        queryKey: ['bump-report-groupby', groupByVariables],
        lineChartQueryConfig: { enabled: false },
        extras: {
            from: tileData.bumpReportTile.key,
            y2AxisLabel: selectedOptionsWithLabels?.length === 1 ? selectedOptionsWithLabels?.[0]?.label : legendLabels,
        },
        url: bumpReportUrls.groupByUrl,
        variables: {
            ...groupByVariables,
        },
    });

    //react query to fetch groupby 1st comparison tab data
    const {
        data: firstComparisonGroupByData,
        loading: firstComparisonGroupByLoading,
        error: firstComparisonGroupByError,
        tableData: firstComparisonGroupByTableData,
    } = useFetchBumpReportGraphRQ({
        method: 'post',
        appConfig,
        queryConfig: { enabled: enabledVariable && isGroupBy && Boolean(groupByComparisonFirstVariable?.isComparison) },
        queryKey: ['bump-report-groupby', groupByComparisonFirstVariable],
        lineChartQueryConfig: { enabled: false },
        extras: {
            from: tileData.bumpReportTile.key,
            y2AxisLabel: selectedOptionsWithLabels?.length === 1 ? selectedOptionsWithLabels?.[0]?.label : legendLabels,
        },
        url: bumpReportUrls.groupByUrl,
        variables: {
            ...groupByComparisonFirstVariable,
        },
    });

    //react query to fetch groupby 2nd comparison tab data
    const {
        data: secondComparisonGroupByData,
        loading: secondComparisonGroupByLoading,
        error: secondComparisonGroupByError,
        tableData: secondComparisonGroupByTableData,
    } = useFetchBumpReportGraphRQ({
        method: 'post',
        appConfig,
        queryConfig: {
            enabled: enabledVariable && isGroupBy && Boolean(groupByComparisonSecondVariable?.isComparison),
        },
        queryKey: ['bump-report-groupby', groupByComparisonSecondVariable],
        lineChartQueryConfig: { enabled: false },
        extras: {
            from: tileData.bumpReportTile.key,
            y2AxisLabel: selectedOptionsWithLabels?.length === 1 ? selectedOptionsWithLabels?.[0]?.label : legendLabels,
        },
        url: bumpReportUrls.groupByUrl,
        variables: {
            ...groupByComparisonSecondVariable,
        },
    });

    //react query to fetch promo/media calender
    const {
        data: promoCalenderData,
        promoTypesObj: distinctPromoTypesObj,
        loading: promoCalenderDataLoading,
        error: promoCalenderDataError,
        hasMoreItems,
        setPromoTypesObj,
        setHasMoreItems,
    } = useFetchPromoCalenderRQ({
        method: 'post',
        appConfig,
        queryConfig: {
            enabled: enabledVariable && Boolean(bumpReportCalenderVariable?.filters),
        },
        queryKey: ['bump-report-calender', bumpReportCalenderVariable],
        url: bumpReportUrls.bumpReportCalenderUrl,
        variables: {
            ...bumpReportCalenderVariable,
        },
    });

    //react query to fetch incremental and promo tab export data
    const {
        data: basePromoExportData,
        error: basePromoExportDataError,
        loading: basePromoExportDataLoading,
        tableData,
    } = useFetchGraphExportRQ({
        method: 'post',
        appConfig,
        extras: { volTile: tileData.volTile },
        queryConfig: { enabled: enabledVariable && !isGroupBy && !incrementalPromoExportVariables.isComparison },
        queryKey: ['bump-report-export', incrementalPromoExportVariables],
        url: bumpReportUrls.incrementsPromoExportUrl,
        variables: {
            ...incrementalPromoExportVariables,
        },
    });

    //react query to fetch groupby tab export data
    const {
        data: groupByExportData,
        error: groupByExportDataError,
        loading: groupByExportDataLoading,
        tableData: groupByTableData,
    } = useFetchGraphExportRQ({
        method: 'post',
        extras: { volTile: tileData.volTile },
        appConfig,
        queryConfig: { enabled: enabledVariable && isGroupBy && !Boolean(groupByVariables?.isComparison) },
        queryKey: ['bump-report-export', groupByVariables],
        url: bumpReportUrls.groupByExportUrl,
        variables: {
            ...groupByVariables,
        },
    });

    const calculateColWidth = (chartConfig: any) => {
        let padding = sliderPaddingMapping[sliderValue] || 10;
        // if (sliderValue === 52) padding = 10;
        return (chartConfig.barWidth + padding) * 4;
    };

    const timePeriodMapping = timePeriodSelection ? JSON.parse(timePeriodSelection) : {};

    function findDifferenceInWeeks(date1: number, date2: number) {
        if (!date1 || !date2) return 0;
        const difference = dayjs.unix(date1).diff(dayjs.unix(date2), 'week');
        return difference + 1;
    }

    //groupby kpi dropdown list
    const groupByKpiDropdownList = useMemo(() => {
        let dropdownData = volGroupByOptionsMapping[tileData.volTile.key];
        const updatedGroupByDropdownData = groupByDropdownData?.map((item: any) => {
            return {
                ...item,
                value: `${item?.value}_${tileData.volTile.dataKey}`,
            };
        });
        if (isMedia) {
            dropdownData = [volGroupByOptionsMapping[tileData.volTile.key]?.[0], ...(updatedGroupByDropdownData || [])];
        }
        setGroupByOptionSelected(dropdownData?.[0]);
        return dropdownData;
    }, [selectedBumpReportTile1Data?.key, tileData.bumpReportTile.key, tileData.volTile.key, groupByDropdownData]);

    const timeBucketValue = useMemo(() => {
        return findDifferenceInWeeks(timePeriodMapping?.['end_timestamp'], timePeriodMapping?.['start_timestamp']);
    }, [timePeriodSelection]);

    const memoisedTableColumnsAlt = useMemo(() => {
        const legendData = Object.keys(distinctPromoTypesObj).map((item) => {
            const { label = item, color = colorConstants.brown } = promoTypeColorMapping?.[item] ?? {};
            return {
                label,
                color,
            };
        });
        const colWidth = calculateColWidth(chartConfig);
        return {
            columns: getBumpReportColumns(
                promoCalenderData,
                legendData,
                toggleCalender,
                colWidth,
                appConfig,
                tileData?.volTile,
                isMedia,
                selectedBumpReportTile1Data,
            ),
            legend: legendData,
        };
    }, [chartConfig, promoCalenderData]);

    function toggleCalender() {
        setIsCalenderExpanded(!isCalenderExpanded);
    }

    const axisValues = useMemo(() => {
        let y1AxisLabel;
        //if tab is group by
        if (isGroupBy) {
            y1AxisLabel =
                groupByOptionSelected.label +
                ' ' +
                (tileData.volTile.key === 'vol' || tileData.volTile.key === 'units'
                    ? ''
                    : formatUnit(tileData.absTile.key, false, appConfig));
        } else {
            const y1AxisLabelMapping = mapAbsVolTile(tileData) || [];
            y1AxisLabel =
                y1AxisLabelMapping.length > 0
                    ? y1AxisLabelMapping[0] + ' ' + y1AxisLabelMapping[1]
                    : `Sales ${fetchCurrency(appConfig)}`;
        }

        return { y1AxisLabel, xAxisLabel };
    }, [tileData, groupByOptionSelected]);

    //tab mapping
    const tabChartColorsMapping: any = {
        increments: {
            data: {
                nonComparison: incrementalData,
                comparison: [firstComparisonIncrementalData, secondComparisonIncrementalData],
                comparisonTable: [firstComparisonIncrementalTableData, secondComparisonIncrementalTableData],
            },
            loading: {
                nonComparison: incrementalDataLoading,
                comparison: [firstComparisonIncrementalDataLoading, secondComparisonIncrementalDataLoading],
            },
            error: {
                nonComparison: incrementalDataError,
                comparison: [firstComparisonIncrementalDataError, secondComparisonIncrementalDataError],
            },
            legend: incrementalData?.legend || [],
        },
        promo: {
            data: {
                nonComparison: incrementalData,
                comparison: [firstComparisonIncrementalData, secondComparisonIncrementalData],
                comparisonTable: [firstComparisonIncrementalTableData, secondComparisonIncrementalTableData],
            },
            loading: {
                nonComparison: incrementalDataLoading,
                comparison: [firstComparisonIncrementalDataLoading, secondComparisonIncrementalDataLoading],
            },
            error: {
                nonComparison: incrementalDataError,
                comparison: [firstComparisonIncrementalDataError, secondComparisonIncrementalDataError],
            },
            legend: incrementalData?.legend || [],
        },
        groupBy: {
            data: {
                nonComparison: groupByData,
                comparison: [firstComparisonGroupByData, secondComparisonGroupByData],
                comparisonTable: [firstComparisonGroupByTableData, secondComparisonGroupByTableData],
            },
            loading: {
                nonComparison: groupByLoading,
                comparison: [firstComparisonGroupByLoading, secondComparisonGroupByLoading],
            },
            error: {
                nonComparison: groupByError,
                comparison: [firstComparisonGroupByError, secondComparisonGroupByError],
            },
            legend: groupByData?.legend || [],
        },
    };
    //chart data
    const chartData = useMemo(() => {
        const legend = tabChartColorsMapping[tileData.bumpReportTile.key].legend;
        const color = legend?.map((item: any) => item?.color);
        const data = tabChartColorsMapping[tileData.bumpReportTile.key].data.nonComparison;
        const loading = tabChartColorsMapping[tileData.bumpReportTile.key].loading.nonComparison;
        const error = tabChartColorsMapping[tileData.bumpReportTile.key].error.nonComparison;
        return { color, data, loading, error, tileData, legend };
    }, [tileData, incrementalData, incrementalDataError, groupByError, groupByData]);

    //chart data
    const chartDataComparison = useMemo(() => {
        const legend = tabChartColorsMapping[tileData.bumpReportTile.key].legend;
        const color = legend?.map((item: any) => item?.color);
        const data = tabChartColorsMapping[tileData.bumpReportTile.key].data.comparison;
        const tableData = tabChartColorsMapping[tileData.bumpReportTile.key].data.comparisonTable;
        const loading = tabChartColorsMapping[tileData.bumpReportTile.key].loading.comparison;
        const error = tabChartColorsMapping[tileData.bumpReportTile.key].error.comparison;
        return {
            color,
            data,
            loading,
            error,
            tileData,
            selectedBumpReportTile1Data,
            selectedBumpReportTile2Data,
            legend,
            tableData,
        };
    }, [
        tileData,
        selectedBumpReportTile1Data,
        selectedBumpReportTile2Data,
        firstComparisonIncrementalData,
        firstComparisonIncrementalDataError,
        firstComparisonGroupByError,
        firstComparisonIncrementalDataLoading,
        firstComparisonGroupByData,
        firstComparisonGroupByLoading,
        secondComparisonGroupByData,
        secondComparisonGroupByLoading,
        secondComparisonIncrementalDataLoading,
        secondComparisonIncrementalData,
        secondComparisonIncrementalDataError,
        secondComparisonGroupByError,
        groupByData,
        incrementalData,
    ]);

    const handleSliderChange = (value: number) => {
        //mixpanel event
        trackEventMixpanel(BUMP_REPORT_BAR_SLIDER_CHANGE, {
            'Date Selected': sliderMarks[value].label,
        });

        if (value === 32) return setSliderValue(26);
        setSliderValue(value);
    };

    //called when clicked on create group button (promo comparison)
    //input
    //   - index:number - index of the query
    //   - dateRange:array - array of date range selected by user
    const createGroupClickHandler = (index: number, dateRange: any[], isSingleSelect: boolean) => {
        const setFunctionArray = [setComparisonFirstVariable, setComparisonSecondVariable];
        const setFunctionGroupByArray = [setGroupByComparisonFirstVariable, setGroupByComparisonSecondVariable];

        //groupby
        if (isGroupBy) {
            const tempVariables = {
                ...groupByVariables,
                time_period: {
                    start_timestamp: isSingleSelect ? dateRange[1] : dateRange[0],
                    end_timestamp: dateRange[1],
                },
                isComparison: true,
            };
            setFunctionGroupByArray[index](tempVariables);
            //incremental and promo
        } else {
            const tempVariables = {
                ...incrementalPromoVariables,
                time_period: {
                    start_timestamp: isSingleSelect ? dateRange[1] : dateRange[0],
                    end_timestamp: dateRange[1],
                },
                isComparison: true,
            };
            setFunctionArray[index](tempVariables);
        }
    };

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !idForRestOfComponentLoading &&
            !timePeriodLoading
        ) {
            //fetch attribute types
            if (!attributeTypeList.length) {
                const data = parseAttributeType(allDimAndAttributeTypeList['dimentions']);
                if (data.length) {
                    setAttributeTypeList(data);
                    setAttributeTypeSelected(data[0]);
                }
            }
            setPromoTypesObj({});
            setHasMoreItems(true);
            setBumpReportCalenderVariable({
                page: 1,
                filters: idForRestOfComponent,
                time_period,
                attribute_levels: promoCalenderAttributeLevels?.[selectedBumpReportTile1Data?.key]?.map(
                    (item: any) => item.value,
                ),
                investment_type: selectedBumpReportTile1Data?.key,
            });
        }
    }, [
        idForRestOfComponent,
        idForRestOfComponentLoading,
        allDimAndAttributeTypeList?.loading,
        timePeriodSelection,
        timePeriodLoading,
        selectedBumpReportTile1Data,
    ]);

    //used to debounce Loading state, so that UI wont flicker
    const { loading: combinedLoading } = useDebounceLoading({
        loadingDependency:
            incrementalDataLoading || groupByLoading || timePeriodLoading || allDimAndAttributeTypeList?.loading,
    });
    const promoCalenderLoading =
        bumpReportCalenderVariable.page === 1 ? combinedLoading || promoCalenderDataLoading : promoCalenderDataLoading;
    useEffect(() => {
        setLoading(combinedLoading);
    }, [combinedLoading]);
    //  console.log(chartData, combinedLoading, 'logging inside');
    const infiniteScrollOnNext = (loading: boolean) => {
        if (!loading) {
            const newPage = bumpReportCalenderVariable.page + 1;
            setBumpReportCalenderVariable({
                ...bumpReportCalenderVariable,
                page: newPage,
            });
        }
    };

    const exportData = isGroupBy ? groupByExportData : basePromoExportData;
    const exportLoading = isGroupBy ? groupByExportDataLoading : basePromoExportDataLoading;
    const exportError = isGroupBy ? groupByExportDataError : basePromoExportDataError;
    const changeCalenderLegendStyle = memoisedTableColumnsAlt.legend.length > 8;
    return (
        <main>
            {/* BUMP CHART */}
            <section className={`${commonClassName} bg-white mt-4`} ref={refForGraph}>
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <h2 className="font-lg m-0 text-headerColor font-medium">{bumpChartHeader}</h2>
                        {bumpReportTile1DataList?.length > 1 && (
                            <TileButton
                                data={bumpReportTile1DataList}
                                onClick={(item: any) => setSelectedBumpReportTile1Data(item)}
                                activeKey={selectedBumpReportTile1Data?.key}
                            />
                        )}
                    </div>
                    <div className="flex gap-4 justify-center items-center">
                        {!isGroupBy && (
                            <div className="flex gap-4 items-center">
                                <p className="font-xs font-medium text-fontGray">
                                    {selectedBumpReportTile1Data?.label}
                                </p>
                                <TileButton
                                    data={bumpReportTile2Data}
                                    onClick={(item: any) => setSelectedBumpReportTile2Data(item)}
                                    activeKey={selectedBumpReportTile2Data?.key}
                                />
                            </div>
                        )}
                        {!isGroupBy ? (
                            <SelectionDropDown
                                className="dropdown-width-multi-select"
                                list={bumpChartY2AxisOptions}
                                checkedListObject={''}
                                eventMixPanelString={BUMP_REPORT_DROPDOWN_CHANGE}
                                disabled={combinedLoading}
                                setSelectedList={setOptionSelected}
                                selectedList={optionSelected || []}
                            />
                        ) : (
                            // <CustomSelect
                            //     className="dropdown-height dropdown-width"
                            //     suffixIcon={<DownSvg />}
                            //     value={optionSelected.value}
                            //     disabled={combinedLoading}
                            //     options={bumpChartY2AxisOptions}
                            //     onChange={(val: any, item: any) => {
                            //         setOptionSelected(item);

                            //         //mixpanel event
                            //         trackEventMixpanel(BUMP_REPORT_DROPDOWN_CHANGE, {
                            //             tab: tileData.bumpReportTile.label,
                            //             'dropdown type': 'KPI',
                            //             'dropdown value': item.label,
                            //         });
                            //     }}
                            // />
                            <div className="flex">
                                <div>
                                    <div className="font-xs">{attributeTypeLabel}</div>
                                    <CustomSelect
                                        className="dropdown-height dropdown-width-alt uniqSelBRGroupByDropdown"
                                        suffixIcon={<DownSvg />}
                                        style={{ marginRight: 16 }}
                                        value={attributeTypeSelected.value}
                                        disabled={combinedLoading}
                                        loading={allDimAndAttributeTypeList?.loading || false}
                                        options={attributeTypeList}
                                        onChange={(val: any, item: any) => {
                                            setAttributeTypeSelected(item);
                                            //mixpanel event
                                            trackEventMixpanel(BUMP_REPORT_GROUPBY_DROPDOWN_CHANGE, {
                                                dropdownName: item.label,
                                            });
                                        }}
                                    />
                                </div>

                                <div>
                                    <div className="font-xs">{kpiDropdownLabel}</div>
                                    <CustomSelect
                                        className="dropdown-height dropdown-width-alt uniqSelBRGrounpByKPIDropdown"
                                        suffixIcon={<DownSvg />}
                                        value={groupByOptionSelected.value}
                                        disabled={combinedLoading || groupByDropdownLoading}
                                        options={groupByKpiDropdownList}
                                        onChange={(val: any, item: any) => {
                                            setGroupByOptionSelected(item);

                                            //mixpanel event
                                            trackEventMixpanel(BUMP_REPORT_GROUPBY_KPI_DROPDOWN_CHANGE, {
                                                dropdownName: item.label,
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        <CopyToClipBoard
                            mixpanelEvent={() => trackEventMixpanel(BUMP_REPORT_CHART_COPY_CLICKED)}
                            refOfComponent={refForGraph}
                        />
                        {exportLoading ? (
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
                        ) : (
                            !exportError && (
                                <DownloadExcel
                                    mixpanelEvent={() => trackEventMixpanel(BUMP_REPORT_CHART_EXPORT_CLICKED)}
                                    excelData={exportData?.data || []}
                                    headers={exportData?.header || []}
                                    title={'bumpReport'}
                                />
                            )
                        )}
                    </div>
                </div>
                {
                    // IF ERROR RETURN ERROR MESSAGE
                    chartData.error && !combinedLoading && !chartData.data ? (
                        <ErrorBox msg={String(chartData.error)} />
                    ) : combinedLoading ? (
                        <div style={{ height: 365, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Loader />
                        </div>
                    ) : (
                        <div>
                            <div className="pl-[80px] flex items-center justify-between my-2">
                                {/* Y1 AXIS */}
                                <p style={{ minWidth: legendWidth }} className={`${yAxisLabelClass} !top-0`}>
                                    {axisValues.y1AxisLabel}
                                </p>
                                {/* LEGEND  */}
                                <div style={{ overflowX: 'auto' }}>
                                    <Legend
                                        style={{
                                            justifyContent: 'initial',
                                            marginInline: 'auto',
                                            width: 'max-content',
                                            margin: 4,
                                            padding: 4,
                                            flex: 'none',
                                        }}
                                        data={chartData?.legend}
                                        showTooltip
                                    />
                                </div>

                                {/* Y2 AXIS */}
                                <p
                                    style={{ width: legendWidth }}
                                    className={`${yAxisLabelClass} !top-0 text-right ${isGroupBy ? 'invisible' : ''}`}
                                >
                                    {selectedOptionsWithLabels?.length === 1
                                        ? selectedOptionsWithLabels?.[0]?.label
                                        : multiSelectionDropdownLabel}
                                </p>
                            </div>

                            {/* CHART COMPONENT */}
                            <div style={{ height: bumpChartHeight }}>
                                <BumpChartContainer
                                    showAllLabels={showAllLabels}
                                    promoCalenderData={promoCalenderData}
                                    isCalenderExpanded={isCalenderExpanded}
                                    chartData={chartData}
                                    tileData={tileData}
                                    sliderValue={sliderValue}
                                    chartConfig={chartConfig}
                                    setChartConfig={setChartConfig}
                                    optionSelected={selectedOptionsWithLabels}
                                    timeBucketValue={timeBucketValue}
                                />
                            </div>
                            <div className="flex justify-between items-center relative top-[-25px] mb-[-25px]">
                                <p className="w-[100px]"></p>
                                <p className="text-greyFont font-xs font-medium">{xAxisLabel}</p>
                                <span id="bump-report-custom-slider" className="mt-4">
                                    <CustomSlider
                                        style={{ width: sliderWidth }}
                                        max={52}
                                        min={12}
                                        step={null}
                                        marks={sliderMarks}
                                        value={sliderValue}
                                        tooltip={{ open: false }}
                                        onChange={handleSliderChange}
                                    />
                                </span>
                            </div>
                        </div>
                    )
                }
            </section>

            {/* PROMO CALENDAR */}
            {enablePromoCalender && (
                <section id={promoCalenderId} className={` bg-white mt-4`}>
                    {!promoCalenderLoading && promoCalenderData && isCalenderExpanded && (
                        <div
                            style={{ top: legendTop }}
                            className={`relative overflow-auto bg-white h-[50px] ml-[161px] mr-[14px] mt-[-16px] flex ${
                                changeCalenderLegendStyle ? 'justify-start' : 'justify-center'
                            } z-10`}
                        >
                            <Legend
                                showTooltip
                                style={
                                    changeCalenderLegendStyle
                                        ? {
                                              minHeight: 'min-content',
                                              maxHeight: '100%',
                                          }
                                        : undefined
                                }
                                data={memoisedTableColumnsAlt.legend}
                            />
                        </div>
                    )}
                    {isCalenderExpanded ? (
                        <InfiniteScroller
                            style={{ overflowX: 'hidden' }}
                            data={promoCalenderData || []}
                            loading={promoCalenderLoading}
                            hasMore={hasMoreItems}
                            height={
                                promoCalenderData && !promoCalenderDataError && promoCalenderData?.length >= 6
                                    ? 350
                                    : 'auto'
                            }
                            onNext={() => infiniteScrollOnNext(promoCalenderLoading)}
                        >
                            <BumpReportCalenderTable
                                style={{
                                    opacity: promoCalenderLoading ? '0.3' : 1,
                                    pointerEvents: promoCalenderLoading ? 'none' : 'auto',
                                }}
                                bordered
                                pagination={false}
                                scroll={{ x: '100%' }}
                                sticky
                                rowKey={(record: any) => record[rowKey]}
                                columns={memoisedTableColumnsAlt?.columns}
                                dataSource={promoCalenderData || []}
                                // loading={combinedLoading}
                            />
                        </InfiniteScroller>
                    ) : (
                        <ExpansionBar
                            titleClassName="font-xs font-bold"
                            title={`${isMedia ? 'Media' : 'Promo'} Calendar`}
                            isExpanded={isCalenderExpanded}
                            onClick={(visible: boolean) => setIsCalenderExpanded(visible)}
                        />
                    )}
                </section>
            )}

            {/* COMPARISON SECTION */}
            <p className="my-4 font-medium font-lg text-headerColor">
                {isMedia ? 'Media' : 'Promo'} {promoComparisonSubHeader}
            </p>
            <PromoComparisonContainer
                showAllLabels={showAllLabels}
                dropDownValues={{ attributeTypeSelected, groupByOptionSelected }}
                createGroupClickHandler={createGroupClickHandler}
                chartConfig={chartConfig}
                chartData={{ chartData: chartDataComparison, axisValues }}
                onCloseComparison={(index: number) => {
                    if (index === 0) setComparisonFirstVariable({});
                    else setComparisonSecondVariable({});
                }}
            />
            <div className="my-4">
                <GrowthAnalysisTable
                    columns={isGroupBy ? groupByTableData?.columns : tableData?.columns}
                    data={isGroupBy ? groupByTableData?.rowsData : tableData?.rowsData}
                    tableLoading={combinedLoading || basePromoExportDataLoading || groupByExportDataLoading}
                />
            </div>
        </main>
    );
};

export default MainSection;
